/**
 * ModuleGuard -- route-level module access control.
 *
 * Blocks direct URL navigation when the tenant's manifest has the
 * module disabled, even if the user's role would otherwise allow access.
 */
import { type ReactNode } from "react";

import { PackageX } from "lucide-react";

import type { ModulesEnabled } from "@/features/manifest/model/types";
import {
  useManifest,
  useModuleEnabled,
  useSchedulingEnabled,
} from "@/features/manifest/model/useManifest";

export type ModuleGate = keyof ModulesEnabled | "scheduling";

interface ModuleGuardProps {
  module: ModuleGate;
  label: string;
  children: ReactNode;
}

export function ModuleGuard({ module, label, children }: ModuleGuardProps) {
  const { manifest, isLoading } = useManifest();
  const schedulingEnabled = useSchedulingEnabled();
  const moduleEnabled = useModuleEnabled(
    module === "scheduling" ? "scheduling_internal" : module,
  );
  const enabled = module === "scheduling" ? schedulingEnabled : moduleEnabled;

  // "Not enabled" is a claim about the org, and until the manifest lands we
  // have nothing to base it on. The module hooks answer false while loading,
  // so rendering the refusal here flashed "Scheduling is not available" on
  // every cold load of a page that was in fact enabled - the kind of wrong
  // answer a reader believes and acts on.
  if (isLoading || !manifest) {
    return <div className="min-h-[60vh]" aria-busy="true" />;
  }

  if (!enabled) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
        <PackageX className="mb-4 h-12 w-12 text-muted-foreground" />
        <h2 className="mb-2 text-lg font-semibold">
          {label} is not available
        </h2>
        <p className="max-w-md text-sm text-muted-foreground">
          This module is not enabled for your organization. Contact your
          administrator if you believe you should have access.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
